import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, Dumbbell, Flame, Salad, Target, Activity, Users, HeartPulse, Trophy, Star, Quote,
} from "lucide-react";
import { KineticText, Reveal, Stagger, StaggerItem } from "../components/motion";
import { SectionLabel, PillButton } from "../components/ui-kit";
import { getTransformations, getTestimonials, getSiteSettings } from "../lib/api";
import LazyImage from "../components/LazyImage";

function Counter({ end, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const dur = 1800;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      setVal(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, end]);
  return <span ref={ref}>{val}{suffix}</span>;
}

const stats = [
  { end: 500, suffix: "+", label: "Clients Coached" },
  { end: 320, suffix: "+", label: "Transformations" },
  { end: 8, suffix: "+", label: "Years Experience" },
  { end: 98, suffix: "%", label: "Client Satisfaction" },
];

const services = [
  { icon: Activity, title: "Online Coaching", desc: "Fully personalised training & nutrition delivered through your phone." },
  { icon: Dumbbell, title: "Strength Coaching", desc: "Build real, functional strength with periodised programming." },
  { icon: Flame, title: "Fat Loss", desc: "Lose fat sustainably without crash diets or endless cardio." },
  { icon: Target, title: "Muscle Gain", desc: "Build lean muscle with progressive overload done right." },
  { icon: HeartPulse, title: "Body Recomposition", desc: "Drop fat and build muscle at the same time, the smart way." },
  { icon: Salad, title: "Nutrition Guidance", desc: "Flexible, macro-based nutrition that fits your real life." },
  { icon: Trophy, title: "Exercise Technique", desc: "Master lifts with video form reviews and cueing." },
  { icon: Users, title: "Accountability", desc: "Weekly check-ins that keep you consistent for life." },
];

const process = [
  { step: "01", title: "Application", desc: "Tell me about your goals, lifestyle and starting point." },
  { step: "02", title: "Consultation", desc: "We hop on a call to map your vision and see if we're a fit." },
  { step: "03", title: "Assessment", desc: "Full analysis of your training, nutrition and habits." },
  { step: "04", title: "Customized Plan", desc: "Your bespoke training & nutrition blueprint is built." },
  { step: "05", title: "Weekly Check-ins", desc: "Ongoing adjustments, form reviews and 24/7 support." },
  { step: "06", title: "Transformation", desc: "You get stronger, leaner and consistent — for life." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [trans, setTrans] = useState([]);
  const [tests, setTests] = useState([]);
  const [site, setSite] = useState({});
  useEffect(() => {
    getTransformations().then(setTrans).catch(() => {});
    getTestimonials().then(setTests).catch(() => {});
    getSiteSettings().then((d) => setSite(d || {})).catch(() => {});
  }, []);

  const heroImg = site.hero_image || "https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
  const aboutImg = site.about_image || "https://customer-assets-lqy194kg.emergentagent.net/job_strength-authority/artifacts/znd6icox_1786026011187682.jpeg";
  const heroLine1 = site.hero_title_1 || "Transform Your Body";
  const heroLine2 = site.hero_title_2 || "Without Guesswork.";
  const heroSub = site.hero_subtitle || "Personalized online fitness coaching designed to help busy professionals lose fat, build muscle, build strength and stay consistent for life.";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: yImg }} className="absolute inset-0 -z-10">
          <img
            src={heroImg}
            alt="Athlete training in a dark gym" className="w-full h-[120%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        </motion.div>

        <motion.div style={{ opacity }} className="max-w-7xl mx-auto px-6 md:px-10 pb-24 pt-40 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <SectionLabel>Premium Online Coaching</SectionLabel>
          </motion.div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.88] max-w-5xl">
            <KineticText lines={[heroLine1, heroLine2]} delay={0.3} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed"
          >
            {heroSub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <PillButton to="/coaching" testid="hero-apply-btn">Apply For Coaching <ArrowRight className="w-4 h-4 ml-2" /></PillButton>
            <PillButton to="/transformations" variant="secondary" testid="hero-results-btn">View Client Results</PillButton>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="py-6 border-y border-border bg-accent overflow-hidden">
        <Marquee autoFill speed={40} gradient={false}>
          {["Strength", "Fat Loss", "Muscle Gain", "Nutrition", "Accountability", "Consistency", "Discipline"].map((w, i) => (
            <span key={i} className="mx-8 text-white font-heading font-black uppercase tracking-tight text-2xl md:text-3xl flex items-center gap-8">
              {w} <Dumbbell className="w-6 h-6" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* SOCIAL PROOF */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1} className="text-center md:text-left">
                <div className="text-5xl md:text-6xl font-heading font-black text-accent tracking-tighter">
                  <Counter end={s.end} suffix={s.suffix} />
                </div>
                <p className="mt-2 text-sm uppercase tracking-widest text-muted-foreground font-semibold">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO / ABOUT PREVIEW */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <Reveal>
              <div className="rounded-3xl overflow-hidden hover-lift">
                <img src={aboutImg}
                  alt="Coach Manav Shah" className="w-full h-[520px] object-cover object-top" />
              </div>
              <div className="absolute -bottom-6 -right-4 md:right-6 glass rounded-2xl px-6 py-4">
                <p className="font-heading font-black text-2xl text-accent">Manav Shah</p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">{site.founder_tagline || "Founder & Head Coach"}</p>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <SectionLabel index="01">The Philosophy</SectionLabel>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-6">
              No fake promises. No flashy gimmicks. Just <span className="text-accent">science that works.</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              I help busy professionals cut through the noise of social media fitness advice and build a body they're proud of — through evidence-based training, sustainable nutrition, and genuine accountability.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Every program is built around your real life: your schedule, your equipment, your preferences. This isn't a template. It's coaching designed to make you consistent for life.
            </p>
            <PillButton to="/about" variant="secondary" testid="home-about-btn">My Full Story <ArrowRight className="w-4 h-4 ml-2" /></PillButton>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-16">
            <SectionLabel index="02">What I Offer</SectionLabel>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
              Coaching built for every goal.
            </h2>
          </div>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s) => (
              <StaggerItem key={s.title}>
                <div data-testid={`service-${s.title.toLowerCase().replace(/ /g,'-')}`}
                  className="glass rounded-2xl p-7 h-full hover-lift group">
                  <span className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-5 group-hover:bg-accent transition-colors">
                    <s.icon className="w-6 h-6 text-accent group-hover:text-white transition-colors" />
                  </span>
                  <h3 className="font-heading font-bold text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section className="py-24 md:py-32 border-t border-border relative overflow-hidden">
        <div className="absolute top-40 left-1/2 w-96 h-96 accent-glow rounded-full -translate-x-1/2" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="max-w-3xl mb-16">
            <SectionLabel index="03">The Journey</SectionLabel>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">Your path to transformation.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {process.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div className="glass rounded-2xl p-8 h-full relative hover-lift">
                  <span className="font-heading font-black text-5xl text-accent/25">{p.step}</span>
                  <h3 className="font-heading font-bold text-xl mt-3 mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSFORMATIONS PREVIEW */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <SectionLabel index="04">Real Results</SectionLabel>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">Transformations that speak.</h2>
            </div>
            <PillButton to="/transformations" variant="secondary" testid="home-transformations-btn">View All <ArrowRight className="w-4 h-4 ml-2" /></PillButton>
          </div>
          {trans.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={`home-trans-sk-${i}`} className="rounded-2xl overflow-hidden glass">
                  <div className="skeleton aspect-[4/5]" />
                  <div className="p-6 space-y-2">
                    <div className="skeleton h-4 w-1/2 rounded" />
                    <div className="skeleton h-3 w-1/3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {trans.slice(0, 3).map((t) => (
                <StaggerItem key={t.id}>
                  <div className="rounded-2xl overflow-hidden glass hover-lift group">
                    {t.composite ? (
                      <div className="relative overflow-hidden aspect-[4/5]">
                        <LazyImage src={t.after} alt={t.name} className="absolute inset-0 transition-transform duration-500 group-hover:scale-105" />
                        <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-accent text-white px-2.5 py-1 rounded-full font-bold z-10">Before / After</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2">
                        <div className="relative overflow-hidden aspect-[3/4]">
                          <LazyImage src={t.before} alt="Before" className="absolute inset-0 transition-transform duration-500 group-hover:scale-105" />
                          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-black/60 text-white px-2 py-1 rounded-full z-10">Before</span>
                        </div>
                        <div className="relative overflow-hidden aspect-[3/4]">
                          <LazyImage src={t.after} alt="After" className="absolute inset-0 transition-transform duration-500 group-hover:scale-105" />
                          <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-accent text-white px-2 py-1 rounded-full z-10">After</span>
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-heading font-bold">{t.name}</h3>
                        <span className="text-accent font-heading font-black">{t.result}</span>
                      </div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">{t.category} • {t.duration}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <SectionLabel index="05">Client Words</SectionLabel>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">Loved by people like you.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {tests.slice(0, 4).map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <div className="glass rounded-2xl p-8 h-full hover-lift">
                  <Quote className="w-8 h-8 text-accent/40 mb-4" />
                  <div className="flex gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-accent text-accent" />)}</div>
                  <p className="text-lg leading-relaxed mb-6">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} loading="lazy" decoding="async" className="w-11 h-11 rounded-full object-cover bg-secondary/40" />
                    <div>
                      <p className="font-heading font-bold text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-40 border-t border-border relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center relative">
          <Reveal>
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-accent">Limited Coaching Spots</span>
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95] mt-5 mb-8">
              Ready to build a body you're proud of?
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-10">
              I take on a limited number of clients to guarantee quality coaching. Apply today and secure your spot.
            </p>
            <PillButton to="/coaching" testid="home-final-cta">Apply For Coaching <ArrowRight className="w-4 h-4 ml-2" /></PillButton>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
